import React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ChevronDown } from "lucide-react"
import { EngagementStatusBadge } from "@/components/ui/engagement-status-badge"
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import type { EngagementResponse } from "@/types/api/engagement"
import { useFormatters } from "@/hooks/use-formatters"
import { cn } from "@/lib/utils"

interface VersionSelectorProps {
  allVersions: EngagementResponse[] | null
  currentVersion?: number
  confirmNavigation?: (onConfirm: () => void) => boolean
}

export function VersionSelector({ allVersions, currentVersion, confirmNavigation }: VersionSelectorProps) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string; version?: string }>()
  const [loadingVersion, setLoadingVersion] = React.useState<number | null>(null)
  const { formatDate } = useFormatters()
  
  const handleVersionClick = (versionNumber: number) => {
    if (!id) return

    // Check for unsaved changes before navigating
    const proceedWithNavigation = () => {
      // Set loading state for immediate feedback
      setLoadingVersion(versionNumber)

      // Always navigate to versioned URL for consistency
      // Use replace to avoid duplicate history entries
      navigate(`/${id}/version/${versionNumber}`, { replace: true })
    }

    // If confirmNavigation is provided, use it to check for unsaved changes
    if (confirmNavigation) {
      const shouldNavigate = confirmNavigation(proceedWithNavigation)
      if (shouldNavigate) {
        proceedWithNavigation()
      }
    } else {
      // No confirmation needed, proceed directly
      proceedWithNavigation()
    }
  }

  // Clear loading state when version changes (load completed)
  React.useEffect(() => {
    if (loadingVersion !== null && currentVersion !== undefined) {
      setLoadingVersion(null)
    }
  }, [currentVersion])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="text-slate-700 hover:bg-slate-100 gap-2 min-w-[120px]"
          disabled={!!loadingVersion}
        >
          {loadingVersion ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              Loading...
            </>
          ) : (
            <>
              <span>Version {currentVersion || 1}</span>
              <ChevronDown className="h-4 w-4" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        {!allVersions ? (
          <DropdownMenuItem disabled>
            Loading versions...
          </DropdownMenuItem>
        ) : allVersions.length === 0 ? (
          <DropdownMenuItem disabled>
            No versions found
          </DropdownMenuItem>
        ) : (
          allVersions
            .sort((a, b) => b.data.version - a.data.version) // Sort by version desc (newest first)
            .map((versionResponse) => {
              const version = versionResponse.data
              const isCurrentVersion = version.version === currentVersion
              
              return (
                <DropdownMenuItem 
                  key={version.version}
                  onClick={() => !loadingVersion && handleVersionClick(version.version)}
                  className={cn(
                    'cursor-pointer',
                    isCurrentVersion ? 'bg-primary/5 pointer-events-none' : '',
                    loadingVersion ? 'cursor-wait opacity-50' : ''
                  )}
                  disabled={!!loadingVersion}
                >
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        Version {version.version}
                      </span>
                      {version.status && (
                        <EngagementStatusBadge status={version.status} />
                      )}
                    </div>
                      
                    <div className="text-xs text-muted-foreground">
                      {version.lastModifiedDateTime && 
                        `Modified: ${formatDate(version.lastModifiedDateTime)}`
                      }
                    </div>
                  </div>
                </DropdownMenuItem>
              )
            })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 